// =============================================================================
// RAPPORT DE RENTABILITÉ - PDF synthèse multi-chantiers
// Export global de la rentabilité de l'entreprise
// IMPORTANT: Tous les montants affichés sont en HT (Hors Taxes)
// =============================================================================

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { sharedStyles, COLORS } from './pdfStyles';
import { formatMontant, formatMontantHT, formatPct } from './pdfUtils';
import { SEUILS_MARGE } from '@/lib/calculs-rentabilite';
import type { ChantierRentabilite, KPIs } from '@/hooks/useChantiersFiltresRentabilite';

// =============================================================================
// STYLES SPÉCIFIQUES AU RAPPORT RENTABILITÉ
// =============================================================================

const styles = StyleSheet.create({
  // KPIs en grille 3x2
  kpiGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 20,
  },
  kpiBox: {
    width: '31%',
    backgroundColor: COLORS.grisClair,
    borderRadius: 6,
    padding: 12,
    alignItems: 'center',
  },
  kpiBoxHighlight: {
    backgroundColor: COLORS.bleuClair,
    borderWidth: 1,
    borderColor: COLORS.bleuRoi,
  },
  kpiBoxDanger: {
    backgroundColor: COLORS.rougeClair,
    borderWidth: 1,
    borderColor: COLORS.rougeAlerte,
  },
  kpiLabel: {
    fontSize: 8,
    color: COLORS.grisTexte,
    textTransform: 'uppercase',
    marginBottom: 4,
    textAlign: 'center',
  },
  kpiValue: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.noir,
    textAlign: 'center',
  },
  kpiUnit: {
    fontSize: 9,
    color: COLORS.grisTexte,
    marginTop: 2,
  },

  // Titre de section
  sectionTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    backgroundColor: COLORS.bleuRoi,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 0,
  },

  // Tableau des chantiers
  tableContainer: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderTopWidth: 0,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: COLORS.bleuRoi,
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
  tableHeaderCell: {
    color: COLORS.blanc,
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  tableRowAlt: {
    backgroundColor: COLORS.grisClair,
  },
  tableRowTotal: {
    backgroundColor: COLORS.bleuRoi,
    borderBottomWidth: 0,
  },
  tableCell: {
    fontSize: 8,
    color: COLORS.grisFonce,
  },
  tableCellBold: {
    fontFamily: 'Helvetica-Bold',
  },
  tableCellRight: {
    textAlign: 'right',
  },
  tableCellWhite: {
    color: COLORS.blanc,
  },

  // Colonnes du tableau
  colChantier: { width: '25%' },
  colClient: { width: '20%' },
  colRevenus: { width: '15%' },
  colCouts: { width: '15%' },
  colMarge: { width: '12%' },
  colTaux: { width: '13%' },

  // Message vide
  emptyMessage: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 12,
    color: COLORS.grisTexte,
    textAlign: 'center',
  },

  // Filtre info
  filterBadge: {
    backgroundColor: COLORS.dore,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginTop: 4,
  },
  filterText: {
    fontSize: 9,
    color: COLORS.bleuRoi,
    fontFamily: 'Helvetica-Bold',
  },
});

// =============================================================================
// PROPS
// =============================================================================

interface RapportRentabilitePDFProps {
  chantiers: ChantierRentabilite[];
  kpis: KPIs;
  filtreActif: string;
  entrepriseNom?: string;
}

// =============================================================================
// UTILITAIRES
// =============================================================================

// Tronquer un texte long
const truncate = (text: string, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

// Couleur du taux de marge
const getMargeColor = (margePct: number): string => {
  if (margePct >= SEUILS_MARGE.BON) return COLORS.vertSucces;
  if (margePct >= SEUILS_MARGE.CRITIQUE) return COLORS.orangeAttention;
  return COLORS.rougeAlerte;
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function RapportRentabilitePDF({
  chantiers,
  kpis,
  filtreActif,
  entrepriseNom = 'B8ild',
}: RapportRentabilitePDFProps) {
  const dateGeneration = format(new Date(), "d MMMM yyyy 'à' HH:mm", { locale: fr });

  // Trier les chantiers par rentabilité décroissante
  const chantiersTries = [...chantiers].sort((a, b) => {
    const margePctA = a.metrics?.marge_finale_pct ?? -Infinity;
    const margePctB = b.metrics?.marge_finale_pct ?? -Infinity;
    return margePctB - margePctA;
  });

  return (
    <Document>
      <Page size="A4" style={sharedStyles.page}>
        {/* ================================================================= */}
        {/* HEADER                                                            */}
        {/* ================================================================= */}
        <View style={sharedStyles.header}>
          <View style={sharedStyles.headerLeft}>
            <Text style={sharedStyles.logoText}>
              B<Text style={{ color: '#F59E0B' }}>8</Text>ild
            </Text>
          </View>
          <View style={sharedStyles.headerRight}>
            <Text style={sharedStyles.headerTitle}>RAPPORT DE RENTABILITÉ</Text>
            <Text style={sharedStyles.headerSubtitle}>Généré le {dateGeneration}</Text>
            <View style={styles.filterBadge}>
              <Text style={styles.filterText}>Filtre : {filtreActif}</Text>
            </View>
          </View>
        </View>
        <View style={sharedStyles.bandeDore} />

        {/* ================================================================= */}
        {/* CONTENU                                                           */}
        {/* ================================================================= */}
        <View style={sharedStyles.content}>
          {/* KPIs en grille 3x2 */}
          <View style={styles.kpiGrid}>
            {/* Chantiers */}
            <View style={styles.kpiBox}>
              <Text style={styles.kpiLabel}>Chantiers</Text>
              <Text style={styles.kpiValue}>{kpis.nombreChantiers}</Text>
            </View>

            {/* Revenus */}
            <View style={styles.kpiBox}>
              <Text style={styles.kpiLabel}>Revenus totaux</Text>
              <Text style={[styles.kpiValue, { color: COLORS.vertSucces }]}>
                {formatMontant(kpis.totalRevenus)}
              </Text>
              <Text style={styles.kpiUnit}>HT</Text>
            </View>

            {/* Coûts */}
            <View style={styles.kpiBox}>
              <Text style={styles.kpiLabel}>Coûts totaux</Text>
              <Text style={[styles.kpiValue, { color: COLORS.orangeAttention }]}>
                {formatMontant(kpis.totalCouts)}
              </Text>
              <Text style={styles.kpiUnit}>HT</Text>
            </View>

            {/* Marge */}
            <View style={[styles.kpiBox, styles.kpiBoxHighlight]}>
              <Text style={styles.kpiLabel}>Marge globale</Text>
              <Text style={[styles.kpiValue, { color: kpis.margeGlobale >= 0 ? COLORS.vertSucces : COLORS.rougeAlerte }]}>
                {formatMontant(kpis.margeGlobale)}
              </Text>
              <Text style={styles.kpiUnit}>HT</Text>
            </View>

            {/* Rentabilité moyenne */}
            <View style={[styles.kpiBox, styles.kpiBoxHighlight]}>
              <Text style={styles.kpiLabel}>Rentabilité moyenne</Text>
              <Text style={[styles.kpiValue, { color: getMargeColor(kpis.rentabiliteMoyenne) }]}>
                {formatPct(kpis.rentabiliteMoyenne)}
              </Text>
            </View>

            {/* En perte */}
            <View style={[styles.kpiBox, kpis.chantiersEnPerte > 0 ? styles.kpiBoxDanger : {}]}>
              <Text style={styles.kpiLabel}>En perte</Text>
              <Text style={[styles.kpiValue, { color: kpis.chantiersEnPerte > 0 ? COLORS.rougeAlerte : COLORS.vertSucces }]}>
                {kpis.chantiersEnPerte}
              </Text>
              <Text style={styles.kpiUnit}>chantier(s)</Text>
            </View>
          </View>

          {/* Titre du tableau */}
          <Text style={styles.sectionTitle}>DÉTAIL PAR CHANTIER</Text>

          {/* Tableau des chantiers ou message vide */}
          {chantiersTries.length === 0 ? (
            <View style={[styles.tableContainer, styles.emptyMessage]}>
              <Text style={styles.emptyText}>Aucun chantier correspondant aux filtres</Text>
            </View>
          ) : (
            <View style={styles.tableContainer}>
              {/* Header du tableau */}
              <View style={styles.tableHeader} wrap={false}>
                <Text style={[styles.tableHeaderCell, styles.colChantier]}>Chantier</Text>
                <Text style={[styles.tableHeaderCell, styles.colClient]}>Client</Text>
                <Text style={[styles.tableHeaderCell, styles.colRevenus, styles.tableCellRight]}>Revenus HT</Text>
                <Text style={[styles.tableHeaderCell, styles.colCouts, styles.tableCellRight]}>Coûts HT</Text>
                <Text style={[styles.tableHeaderCell, styles.colMarge, styles.tableCellRight]}>Marge</Text>
                <Text style={[styles.tableHeaderCell, styles.colTaux, styles.tableCellRight]}>Taux</Text>
              </View>

              {/* Lignes du tableau */}
              {chantiersTries.map((chantier, index) => {
                const hasMetrics = chantier.metrics !== null;
                const couts = hasMetrics
                  ? (chantier.metrics!.couts_fixes_engages + chantier.metrics!.cout_main_oeuvre_reel)
                  : 0;
                const marge = hasMetrics ? chantier.metrics!.marge_finale : 0;
                const margePct = hasMetrics ? chantier.metrics!.marge_finale_pct : 0;

                return (
                  <View
                    key={chantier.id}
                    style={[styles.tableRow, index % 2 === 1 ? styles.tableRowAlt : {}]}
                    wrap={false}
                  >
                    <Text style={[styles.tableCell, styles.colChantier, styles.tableCellBold]}>
                      {truncate(chantier.nom_chantier, 28)}
                    </Text>
                    <Text style={[styles.tableCell, styles.colClient]}>
                      {truncate(chantier.client, 20)}
                    </Text>
                    <Text style={[styles.tableCell, styles.colRevenus, styles.tableCellRight]}>
                      {formatMontant(chantier.budget_ht)}
                    </Text>
                    <Text style={[styles.tableCell, styles.colCouts, styles.tableCellRight]}>
                      {hasMetrics ? formatMontant(couts) : '-'}
                    </Text>
                    <Text style={[styles.tableCell, styles.colMarge, styles.tableCellRight]}>
                      {hasMetrics ? formatMontant(marge) : '-'}
                    </Text>
                    <Text
                      style={[
                        styles.tableCell,
                        styles.colTaux,
                        styles.tableCellRight,
                        styles.tableCellBold,
                        { color: hasMetrics ? getMargeColor(margePct) : COLORS.grisTexte },
                      ]}
                    >
                      {hasMetrics ? formatPct(margePct) : '-'}
                    </Text>
                  </View>
                );
              })}

              {/* Ligne de total */}
              <View style={[styles.tableRow, styles.tableRowTotal]} wrap={false}>
                <Text style={[styles.tableCell, styles.colChantier, styles.tableCellBold, styles.tableCellWhite]}>
                  TOTAL ({kpis.nombreChantiers} chantiers)
                </Text>
                <Text style={[styles.tableCell, styles.colClient, styles.tableCellWhite]} />
                <Text style={[styles.tableCell, styles.colRevenus, styles.tableCellRight, styles.tableCellBold, styles.tableCellWhite]}>
                  {formatMontant(kpis.totalRevenus)}
                </Text>
                <Text style={[styles.tableCell, styles.colCouts, styles.tableCellRight, styles.tableCellBold, styles.tableCellWhite]}>
                  {formatMontant(kpis.totalCouts)}
                </Text>
                <Text style={[styles.tableCell, styles.colMarge, styles.tableCellRight, styles.tableCellBold, styles.tableCellWhite]}>
                  {formatMontant(kpis.margeGlobale)}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    styles.colTaux,
                    styles.tableCellRight,
                    styles.tableCellBold,
                    { color: COLORS.dore },
                  ]}
                >
                  {formatPct(kpis.rentabiliteMoyenne)}
                </Text>
              </View>
            </View>
          )}
        </View>

        {/* ================================================================= */}
        {/* FOOTER                                                            */}
        {/* ================================================================= */}
        <View style={sharedStyles.footer} fixed>
          <View style={sharedStyles.footerBande} />
          <View style={sharedStyles.footerContent}>
            <Text style={sharedStyles.footerText}>
              B8ild — Généré le {format(new Date(), 'dd/MM/yyyy', { locale: fr })} à {format(new Date(), 'HH:mm', { locale: fr })}
            </Text>
            <Text
              style={sharedStyles.footerPage}
              render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`}
            />
          </View>
        </View>
      </Page>
    </Document>
  );
}

export default RapportRentabilitePDF;
